import type { CSSProperties } from "react";
import { DSRadius } from "../foundations/tokens";

export type SkeletonShape = "rectangle" | "circle" | "capsule";

export interface SkeletonProps {
	/** Number of stacked rectangle lines (default 3). Ignored when `shape` is set. */
	readonly rows?: number;
	readonly shape?: SkeletonShape;
	readonly width?: number | string;
	readonly height?: number | string;
	readonly className?: string;
}

/**
 * Skeleton — shimmering placeholder. Supports the legacy `rows` API (stacked
 * rectangles) and explicit `shape` variants (rectangle / circle / capsule).
 */
export function Skeleton({
	rows = 3,
	shape,
	width,
	height,
	className = "",
}: Readonly<SkeletonProps>) {
	if (shape !== undefined) {
		const style: CSSProperties = {
			display: "block",
			width: typeof width === "number" ? `${width}px` : (width ?? "100%"),
			height:
				typeof height === "number"
					? `${height}px`
					: (height ?? `${DSRadius.xl}px`),
			borderRadius:
				shape === "circle"
					? "50%"
					: shape === "capsule"
						? "9999px"
						: "var(--ds-radius-lg)",
			background:
				"linear-gradient(90deg, var(--ds-surface), color-mix(in display-p3, var(--ds-surface) 72%, var(--ds-text-secondary)), var(--ds-surface))",
			backgroundSize: "200% 100%",
			animation: "ds-shimmer 1.4s linear infinite",
		};
		return (
			<span
				className={`ds-skeleton ds-skeleton--${shape} ${className}`.trim()}
				style={style}
				aria-hidden="true"
			/>
		);
	}
	return (
		<div className={`ds-skeleton ${className}`.trim()} aria-hidden="true">
			{Array.from({ length: rows }, (_, index) => {
				// biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are static placeholders that never reorder
				return <span key={`skeleton-row-${index}`} />;
			})}
		</div>
	);
}
